<script>
   /**************************************************************/
   /* AppContainer for target predictions                        */
   /* -----------------------------------                        */
   /* app container for testing of SIMCA model                   */
   /* on target class                                            */
   /**************************************************************/

   import { downloadResults, downloadModel, simca_predict } from '../utils';

   import AppPlots from './AppPlots.svelte';
   import ResultTable from './ResultTable.svelte';

   import DataLoader from '../../../shared/components/PCADataLoader.svelte';
   import ModelLoader from '../shared/ModelLoader.svelte';
   import ModelTable from '../shared/ModelTable.svelte';
   import SIMCASettings from '../shared/SIMCASettings.svelte';

   import Container from '../../../shared/controls/Container.svelte';
   import Button from '../../../shared/controls/Button.svelte';
   import Outliers from '../../../shared/widgets/Outliers.svelte';
   import ModalContributions from '../modals/Contributions.svelte';


   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   export let WASM; // WASM object from parent app


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // main properties of the test app
   let model;                 // PCA model
   let simca;                 // SIMCA parameters
   let testset;               // test set

   // parameters to check if model or simca object have been updated
   let modelHash = 0;
   let simcaHash = 0;

   // settings for outliers
   let outliers = [];         // vector with dataset row numbers (from 0) marked as outliers

   // settings for results
   let testres;               // PCA results for the test set
   let simcares;              // combined results for SIMCA classification
   let ncomp = 0;             // number of components selected by user

   // extra variables to combine PCA results and dataset for scores plot
   let pcares = undefined;
   let datasets = undefined;

   // flag and object id container for showing contributions plot
   let isContributionsModalShown = false;
   let outlierId = -1;


   /**
    * Show contributions plot for the selected object
    * @param e - event dispatched by AppPlots component with id of the selected object
    */
   function showContributionsModal(e) {
      const pos = parseInt(e.detail.id);
      if (Number.isNaN(pos) || pos < 0 || pos >= testres.objindices.length ) return;

      // find sample ID corresponding to the position
      const id = testres.objindices[pos];
      if (id >= 0 && !outliers.includes(id) && testres.objindices.includes(id)) {
         outlierId = id;
         isContributionsModalShown = true;
      }
   }


   /**
    * Close contributions plot
    * @param e - event dispatched by ContributionsPlot component
    */
   function closeContributionsModal(e) {
      outlierId = -1;
      isContributionsModalShown = false;
   }


   /**
    * Set the selected point as outlier and reset the model and results
    * @param e - event dispatched by AppPlots component with id of the selected point
    */
   function setOutlier(e) {
      // get position (index) of the sample to be removed
      const pos = parseInt(e.detail.id);
      if (Number.isNaN(pos) || pos < 0 || pos >= testres.objindices.length ) return;

      // find sample ID corresponding to the position
      const id = testres.objindices[pos];

      // filter the outlier out
      if (id >= 0 && !outliers.includes(id)) {
         outliers = [...outliers, id];
      }
   }


   /**
    * Reset outliers and samples arrays
    */
   function resetOutliers() {
      outliers = [];
   }

   // if model is null - reset dependent objects
   $: if (model === null) {
      simca = null;
      testset = null;
      testres = null;
      ncomp = 0;
      modelHash = 0;
      simcaHash = 0;
   }

   // if dataset is null - reset dependent objects
   $: if (testset === null) {
      testres = null;
      simcares = null;
      outliers = [];
   }


   // check hashes and initiate updates if outcomes changed
   $: if (model && model.hash !== modelHash) {
      modelHash = model.hash;
   }

   $: if (simca && testres) {
      simcaHash = simca.hash;
      simcares = {
         target: simca_predict(testres, simca, outliers)
      };

      // extra containers for PCA results and datasets
      pcares = {target: testres};
      datasets = {target: testset};
   }
</script>

{#if isContributionsModalShown}
<ModalContributions data={testset} {model} {ncomp} {outliers} selected={outlierId} on:close={closeContributionsModal} />
{/if}

<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="testset-loader">
         <ModelLoader bind:model={model} bind:simca={simca} />
      </div>

      {#if model}
      <div class="testset-loader">
         <DataLoader bind:dataset={testset} bind:res={testres} {WASM} {model} {outliers}
         classMandatory={false} classUnique={false}/>
      </div>
      {/if}

      {#if outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers dataset={testset} on:resetoutliers={resetOutliers} bind:outliers={outliers} />
      </div>
      {/if}

      {#if simca && testset}
      <div class="simca-settings">
         <SIMCASettings {model} bind:simca={simca} bind:ncomp={ncomp} />
         <Container label='Download'>
            <div class="buttons">
            <Button on:click={() => downloadResults(simcares.target, testset, "target", outliers, ncomp, model.exclvars)}>Results</Button>
            <Button on:click={() => downloadModel(model, simca, ncomp)}>Model</Button>
            </div>
         </Container>
      </div>
      <div class="model-stats">
         <ModelTable {simca} {ncomp} />
      </div>
      {/if}
      {#if simcares}
      <div class="result-stats">
         <ResultTable res={simcares.target} {ncomp} />
      </div>
      {/if}

   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !testres}
         <div class="help-text">
         <p>
            In this tab you can apply previously trained DD-SIMCA model to a dataset consisting of target class members (at least
            the app will assume this, you can of course provide any dataset). You can use it for testing performance
            of the model, or tune its parameters, if necessary.
         </p>
         {#if !model}
            <p><strong>Step 1</strong></p>
            <p>
               Load previously trained DD-SIMCA model from file.
            </p>
         {:else}
            {#if !testset}
            <p><strong>Step 2</strong></p>
            <p>
               Load testset set from CSV file. The requirements to the file are the same as for the
               training set. The testset may contain class labels for each row (you need to select this option in settings after selecting CSV file) but it is not necessary, as in any case the labels will be ignored and model will assume that all samples are from the target class.
            </p>
            {/if}
         {/if}
         </div>
      {:else}
         {#key testset || outliers}
         <AppPlots on:setoutlier={setOutlier} on:plotoutlier={showContributionsModal}
         {model} {pcares} {simcares} {ncomp} {datasets} />
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0 1em 0 0.5em;
      padding: 0.25em 0.25em;
   }

   .app-controls > div {
      background: #e8e8e8;
      border-radius: 0.25rem;
   }

   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }

   .model-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.5rem;
      font-size: 0.85em;
   }

   .result-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }

   .buttons {
      position: relative;
      box-sizing: border-box;
      display: inline-block;
      flex: 1 1 auto;
      margin: 0;
      padding: 2px 0;
      width: auto;
      border-radius: 2px;
      cursor: default;
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(320px, 30%) 1fr;
      }
   }

</style>